import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { Page as PageIcon } from 'iconoir-react'
import moment from 'moment'
import { useContext, useMemo } from 'react'
import { ErrorRow, ImportWizardContext } from '../ImportWizard'
import { Modal } from '../Modal'
import SimpleTable from '../table/SimpleTable'
import { formatDate } from 'app/date.utils'

export default function Step5() {
  const { csvFile, openErrorModal, importResults, setOpenErrorModal } = useContext(ImportWizardContext)
  let errorColumns = useMemo(
    (): ColumnDef<ErrorRow, any>[] => [
      {
        id: `rowNumber`,
        header: `Row Number`,
        accessorFn: (row) => row.rowNumber,
        meta: {
          defaultVisible: true,
          sortKey: `rowNumber`,
        },
        enableSorting: true,
      },
      {
        id: `message`,
        header: `Message`,
        accessorFn: (row) => row.message,
        meta: {
          defaultVisible: true,
          sortKey: `message`,
        },
        enableSorting: true,
      },
    ],
    [importResults.error],
  )
  return (
    <>
      <div className="flex w-full flex-col md:w-[622px]">
        <h5>Upload summary</h5>
        <div className="mb-10 mt-8 flex items-center gap-x-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-default bg-surface p-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-surface-subtle">
              <PageIcon height={20} width={20} className="stroke-current text-subtle" />
            </div>
          </div>
          <div className="flex flex-col gap-y-2">
            <span
              className={classNames(`text-heading-xs`, {
                [`text-green-500`]: importResults.importIds.length > 0 && importResults.error.length === 0,
                [`text-amber-500`]: importResults.importIds.length > 0 && importResults.error.length > 0,
                [`text-red-500`]: importResults.importIds.length === 0 && importResults.error.length > 0,
              })}
            >
              {importResults.importIds.length > 0 && importResults.error.length === 0 && `Successful import`}
              {importResults.importIds.length > 0 && importResults.error.length > 0 && `Partial import`}
              {importResults.importIds.length === 0 && importResults.error.length > 0 && `Import failed`}
            </span>
            {!!importResults.importIds.length && (
              <span className="text-body-xs text-minimal">
                {csvFile!.name}
                {` ∙ Uploaded ${formatDate({ value: moment.utc(), format: `D MMMM YYYY` })} `}
              </span>
            )}
          </div>
        </div>
        <div className="flex w-full flex-col gap-6">
          <h6>Imported to database</h6>
          <div className="flex items-center justify-between gap-y-4">
            <div className="flex items-center gap-x-3">
              <CheckIcon height={24} width={24} className="stroke-current p-0.5 text-green-500" />
              <span className="text-strong">Imported to database</span>
            </div>
            <span>{importResults.importIds.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-x-3">
              <XMarkIcon height={24} width={24} className="stroke-current p-0.5 text-red-500" />
              <span className="text-strong">Import failed</span>
            </div>
            <span>{importResults.error.length}</span>
          </div>
        </div>
      </div>
      {/* eslint-disable-next-line deprecation/deprecation */}
      <Modal
        title="Errors on import"
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        isFullScreen={false}
        is80Percent
      >
        <SimpleTable
          columns={errorColumns}
          data={importResults.error}
          initialSort={[{ id: `rowNumber`, desc: false }]}
          hideFooter
        />
      </Modal>
    </>
  )
}
